import { Button, Divider, Grid, Paper, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import React from 'react';

import CollapseSection from 'components/collapse-section';
import { useCollapseSections } from 'components/collapse-section/collapse-section-hook';
import { FileGrid } from 'components/file-grid/file-grid';
import { LinkField } from 'components/link-field/link-field';
import { ReadOnlyField } from 'components/read-only-field/read-only-field';
import { RegisterDetailComponent } from 'modules/register/register-detail';
import { RegisterField } from 'modules/register/register-field';
import {
  decideRelationLabel,
  handleOriginatorLink,
} from 'modules/register/reqister-utils';
import { ArchivalAidMetadata, PrivateArchivalAid } from 'types';
import { isDevelopment } from 'utils';
import { getSpaceOccupiedLabel } from 'enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
  })
);

type Props = {
  item?: ArchivalAidMetadata;
  isPrivate?: boolean;
  privateItem?: PrivateArchivalAid;
};

export function ArchivalAidDetail({ item, isPrivate, privateItem }: Props) {
  const classes = useStyles();
  const {
    handleCollapseAll,
    handleExpandAll,
    handleStateChange,
    allCollapsed,
    allExpanded,
    isExpanded,
    registerKey,
  } = useCollapseSections();

  const {
    name,
    archivalCollection,
    evidenceNumber,
    type,
    timeRange,
    authors = [],
    note,
    yearOfOrigin,
    publicCopy,
    eadNode,
  } = isPrivate ? privateItem?.archivalAidMetadata || {} : item || {};

  const originYear = yearOfOrigin ? yearOfOrigin.toString() : undefined;
  const structMapFiles = isPrivate
    ? privateItem?.structMap?.subDivisions?.filter((div) => div.fileRef) ?? []
    : publicCopy?.structMap?.subDivisions?.filter((div) => div.fileRef) ?? [];

  return (
    <>
      {isDevelopment() && <>DEV: AID DETAIL</>}

      <Typography variant="h5">{name}</Typography>
      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <Typography variant="h6">Zařazení</Typography>
          <br />
          <br />
          <LinkField
            label="Fond:"
            value={archivalCollection?.name}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          <ReadOnlyField
            label="Číslo fondu:"
            value={archivalCollection?.evidenceNumber}
          />
          <ReadOnlyField label="Úroveň:" value="archivní pomůcka" />
        </Grid>
      </Paper>
      <Grid
        container
        className={classes.root}
        style={{ justifyContent: 'flex-end', paddingBottom: 0 }}
      >
        <Button
          disabled={!allCollapsed()}
          onClick={() => {
            handleCollapseAll();
          }}
        >
          Sbalit vše
        </Button>
        <Button
          disabled={!allExpanded()}
          onClick={() => {
            handleExpandAll();
          }}
        >
          Rozbalit vše
        </Button>
      </Grid>
      <Paper elevation={0} className={classes.paper}>
        <CollapseSection
          overrideOpen={isExpanded('zahlavi')}
          title="Záhlaví"
          id="zahlavi"
          onStateChange={handleStateChange}
          registerKey={registerKey}
        >
          <Grid container className={classes.root}>
            <ReadOnlyField label="Číslo pomůcky:" value={evidenceNumber} />
            <ReadOnlyField label="Název:" value={name} />
            <ReadOnlyField label="Časový rozsah:" value={timeRange} />
            <ReadOnlyField label="Druh pomůcky:" value={type} />
            {authors.map((o, i) => (
              <RegisterField
                key={o?.id}
                label={i === 0 ? 'Autoři:' : ''}
                withoutBorder={true}
                value={o?.preferredName?.label}
                valueLink={o && handleOriginatorLink(o, true)}
              />
            ))}
            <ReadOnlyField label="Rok vytvoření pomůcky" value={originYear} />
            <ReadOnlyField label="Poznámka" value={note} />
          </Grid>
        </CollapseSection>
      </Paper>
      {/* UVOD K POMUCCE. */}
      {eadNode && (
        <Paper elevation={0} className={classes.paper}>
          <CollapseSection
            overrideOpen={isExpanded('uvod')}
            title="Úvod k pomůcce"
            id="uvod"
            onStateChange={handleStateChange}
            registerKey={registerKey}
          >
            {eadNode?.colophon?.originators && (
              <Paper elevation={0} className={classes.paper}>
                <Typography variant="h6">
                  Dějiny původce archivního souboru
                </Typography>
                <br />
                <Grid container className={classes.root}>
                  {eadNode?.colophon?.originators?.map?.((o, i) => (
                    <Grid item xs={12} key={o?.id}>
                      <CollapseSection
                        title={
                          <RegisterField
                            key={o?.id}
                            label={i === 0 ? 'Původce archiválií:' : ''}
                            withoutBorder={true}
                            value={o?.preferredName?.label}
                            valueLink={o && handleOriginatorLink(o, true)}
                          />
                        }
                      >
                        {o && <RegisterDetailComponent detail={o} />}
                      </CollapseSection>
                    </Grid>
                  ))}
                </Grid>
                <Divider />
              </Paper>
            )}

            {/* Dějiny archivního souboru */}
            {eadNode?.custodHist && (
              <Paper elevation={0} className={classes.paper}>
                <Typography variant="h6">Dějiny archivního souboru</Typography>
                <br />
                <Grid container className={classes.root}>
                  <ReadOnlyField
                    label="Dějiny jednotky popisu"
                    value={eadNode.custodHist}
                  />
                  <ReadOnlyField
                    label="Přímý zdroj akvizice"
                    value={eadNode.acqInfo}
                  />
                </Grid>
                <Divider />
              </Paper>
            )}
            {/* Archivní charakteristika archivního souboru */}
            {eadNode && (
              <Paper elevation={0} className={classes.paper}>
                <Typography variant="h6">
                  Archivní charakteristika archivního souboru
                </Typography>
                <br />
                <Grid container className={classes.root}>
                  <ReadOnlyField
                    label="Způsob uspořádání jednotky popisu"
                    value={eadNode.arrangement}
                  />
                  <ReadOnlyField
                    label="Budoucí přírůstky"
                    value={eadNode.accruals}
                  />
                  <ReadOnlyField
                    label="Fyzický stav"
                    value={eadNode.physTech}
                  />
                  <ReadOnlyField
                    label="Pravidla zpracování"
                    value={eadNode?.rules}
                  />
                </Grid>
                <Divider />
              </Paper>
            )}
            {/* Tematický popis archivního souboru. */}
            {eadNode && (
              <Paper elevation={0} className={classes.paper}>
                <Typography variant="h6">
                  Tematický popis archivního souboru
                </Typography>
                <br />
                <Grid container className={classes.root}>
                  <ReadOnlyField
                    label="Tematický popis jednotky popisu"
                    value={eadNode.scopeContent}
                  />
                </Grid>
                <Divider />
              </Paper>
            )}
            {/* Záznam o uspořádání archivního souboru a sestavení archivní pomůcky */}
            {eadNode && (
              <Paper elevation={0} className={classes.paper}>
                <Typography variant="h6">
                  Záznam o uspořádání archivního souboru a sestavení archivní
                  pomůcky
                </Typography>
                <br />
                <Grid container className={classes.root}>
                  {eadNode?.colophon?.arrangers?.map((o, i) => (
                    <RegisterField
                      key={o?.id}
                      label={
                        i === 0 ? 'Zpracovatel úvodu archivní pomůcky:' : ''
                      }
                      withoutBorder={true}
                      value={o?.preferredName?.label}
                      valueLink={o && handleOriginatorLink(o, true)}
                    />
                  ))}
                  <ReadOnlyField
                    label="Datum popisu"
                    value={eadNode?.colophon?.descriptionDate}
                  />
                  <ReadOnlyField
                    label="Datum a místo vydání"
                    value={eadNode?.colophon?.releaseDatePlace}
                  />
                  <ReadOnlyField
                    label="	Archivní pomůcku schválil:"
                    value={eadNode?.colophon?.findingAidApprovedBy}
                  />
                </Grid>
              </Paper>
            )}
          </CollapseSection>
        </Paper>
      )}
      {/* TIRAZ. */}
      {eadNode && (
        <Paper elevation={0} className={classes.paper}>
          <CollapseSection
            overrideOpen={isExpanded('tiraz')}
            title="Tiráž"
            id="tiraz"
            onStateChange={handleStateChange}
            registerKey={registerKey}
          >
            <Grid container className={classes.root}>
              <ReadOnlyField
                label="Evidenční jednotky"
                value={(eadNode?.evidenceUnits ?? [])
                  .map((eu) => `${eu.unitKindLabel} (${eu.quantity})`)
                  .join(', ')}
              />
              {(eadNode?.spaceOccupied ?? []).map((so, index) => (
                <ReadOnlyField
                  key={`so-${index}-${so.unitKind}`}
                  label={getSpaceOccupiedLabel(so)}
                  value={so.quantity}
                />
              ))}

              <ReadOnlyField
                label="Stav archivní pomůckou zpřístupněných archiválií ke dni"
                value={eadNode?.colophon?.findingAidDate}
              />
              <ReadOnlyField
                label="Zpracovatel archivního souboru"
                value={eadNode?.colophon?.arrangerBrief}
              />
              <ReadOnlyField
                label="Archivní pomůcku sestavil"
                value={eadNode?.colophon?.findingAidEditor}
              />
              <ReadOnlyField
                label="Jazyky jednotek popisu"
                value={eadNode?.langMaterial?.join(', ')}
              />
              <ReadOnlyField
                label="Převažující jazyky jednotek popisu"
                value={eadNode?.majorityLangMaterial?.join(', ')}
              />
            </Grid>
          </CollapseSection>
        </Paper>
      )}

      {structMapFiles.length > 0 && (
        <Paper elevation={0} className={classes.paper}>
          <CollapseSection
            title="Ke stažení"
            overrideOpen={isExpanded('download')}
            id="download"
            onStateChange={handleStateChange}
            registerKey={registerKey}
          >
            <Grid container className={classes.root}>
              <FileGrid data={structMapFiles} />
            </Grid>
          </CollapseSection>
        </Paper>
      )}
      {eadNode?.relations && eadNode?.relations.length > 0 && (
        <Paper elevation={0} className={classes.paper}>
          <CollapseSection
            overrideOpen={isExpanded('register')}
            id="register"
            onStateChange={handleStateChange}
            registerKey={registerKey}
            title="Rejstříková hesla"
          >
            <Grid container className={classes.root}>
              {eadNode?.relations?.map((r, i) => (
                <RegisterField
                  key={r?.id}
                  label={decideRelationLabel(r)}
                  withoutBorder={true}
                  value={r?.originator.preferredName?.label}
                  valueLink={
                    r.originator && handleOriginatorLink(r.originator, true)
                  }
                />
              ))}
            </Grid>
          </CollapseSection>
        </Paper>
      )}
    </>
  );
}
